
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { GroupBlockConfiguration } from "@/types/block-configurations";
import {
  BackgroundControls,
  ResponsiveControls,
} from "../shared";

interface GroupBlockPropertiesProps {
  config: GroupBlockConfiguration;
  onChange: (config: GroupBlockConfiguration) => void;
}

/**
 * GroupBlockProperties Component
 *
 * Groups mirror MJML's mj-group: columns inside stay side by side on mobile.
 * Only settings the email renderer honors are exposed (no gap/padding/border).
 */
export function GroupBlockProperties({ config, onChange }: GroupBlockPropertiesProps) {
  const updateField = <K extends keyof GroupBlockConfiguration>(
    field: K,
    value: GroupBlockConfiguration[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Columns in a group stay side by side on mobile instead of stacking.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium mb-1 block">Direction</Label>
          <Select
            value={config.direction}
            onChange={(e) => updateField('direction', e.target.value as GroupBlockConfiguration['direction'])}
          >
            <option value="ltr">Left to Right</option>
            <option value="rtl">Right to Left</option>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-medium mb-1 block">Vertical Align</Label>
          <Select
            value={config.itemsAlign}
            onChange={(e) => updateField('itemsAlign', e.target.value as GroupBlockConfiguration['itemsAlign'])}
          >
            <option value="stretch">Stretch</option>
            <option value="start">Top</option>
            <option value="center">Middle</option>
            <option value="end">Bottom</option>
          </Select>
        </div>
      </div>

      <Accordion type="multiple" defaultValue={["background"]} className="w-full">

        <AccordionItem value="background">
          <AccordionTrigger>Background</AccordionTrigger>
          <AccordionContent>
            <BackgroundControls
              value={config.background}
              onChange={(background) => updateField('background', background)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="responsive">
          <AccordionTrigger>Responsive</AccordionTrigger>
          <AccordionContent>
            <ResponsiveControls
              value={config.responsive}
              onChange={(responsive) => updateField('responsive', responsive)}
            />
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </div>
  );
}
